// V34.7 — Refer & Earn placeholder page matching the BookMyTutor look.
// Tutors refer parents/students and earn when the referee joins. This page
// captures the lead and POSTs to /api/callback-request with cta_source='referral'
// so the existing inquiries/registrations split (V32) handles routing.

import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Info, User, Phone, BookOpen, Loader2, Sparkles } from "lucide-react";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import TutorMobileLayout from "../components/tutor/TutorMobileLayout";

export default function TutorRefer() {
    const [form, setForm] = useState({
        parent_name: "", phone: "", num_students: "1",
        grade: "", subjects: "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.parent_name.trim() || !form.phone.trim()) {
            toast.error("Parent name and phone are required");
            return;
        }
        setBusy(true);
        try {
            // Route via callback-request with cta_source=referral → lands as
            // inquiries with subject="Callback Request" + cta_source="referral".
            await tutorApi.post("/callback-request", {
                name: form.parent_name,
                phone: form.phone,
                cta_source: "referral",
                interest: `${form.grade || ""} ${form.subjects || ""}`.trim() || "Referral from tutor",
            });
            toast.success("Referral submitted — we'll reach out to the parent shortly!");
            setForm({ parent_name: "", phone: "", num_students: "1", grade: "", subjects: "" });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <TutorMobileLayout title="Refer & Earn">
            {/* Hero illustration block */}
            <div className="-mx-4 -mt-4 bg-white px-6 pt-2 pb-6 text-center relative" data-testid="tutor-refer-hero">
                <div className="w-full h-48 rounded-2xl bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 flex items-center justify-center relative overflow-hidden">
                    <Sparkles className="w-20 h-20 text-amber-500 absolute top-6 left-1/2 -translate-x-1/2 opacity-90" />
                    <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-6">
                        <div className="w-16 h-16 rounded-full bg-rose-100 flex items-center justify-center">
                            <User className="w-8 h-8 text-rose-500" />
                        </div>
                        <div className="text-3xl">🤝</div>
                        <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center">
                            <User className="w-8 h-8 text-indigo-500" />
                        </div>
                    </div>
                </div>
                <h1 className="font-heading text-3xl font-extrabold text-rose-600 mt-5">Refer Students &amp; Earn</h1>
                <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                    Refer parents/students who need tuition. You earn only when the referred student joins and starts learning.
                </p>
            </div>

            {/* Important callout */}
            <div className="mb-5 mt-4 rounded-2xl bg-amber-50 border border-amber-200 p-4 flex gap-3" data-testid="tutor-refer-important">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-[13px] text-amber-900 leading-relaxed">
                    <span className="font-bold">Important:</span> To earn referral rewards, you need to refer students/parents, not teachers or tutors. If a parent has more than one student, add grade and subjects for every student.
                </p>
            </div>

            {/* Form */}
            <form onSubmit={submit} className="space-y-3" data-testid="tutor-refer-form">
                <h2 className="font-heading text-xl font-bold text-rose-600 mt-2">Refer Student Details</h2>

                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-3 min-h-[52px]">
                    <User className="w-5 h-5 text-rose-500 shrink-0" />
                    <input
                        value={form.parent_name}
                        onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
                        placeholder="Parent Name"
                        className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
                        data-testid="tutor-refer-parent-name"
                        required
                    />
                </div>

                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-3 min-h-[52px]">
                    <Phone className="w-5 h-5 text-rose-500 shrink-0" />
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        placeholder="Parent Phone"
                        className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
                        data-testid="tutor-refer-parent-phone"
                        required
                    />
                </div>

                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-3 min-h-[52px]">
                    <BookOpen className="w-5 h-5 text-rose-500 shrink-0" />
                    <input
                        value={form.grade}
                        onChange={(e) => setForm({ ...form, grade: e.target.value })}
                        placeholder="Grade (e.g., 8)"
                        className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
                        data-testid="tutor-refer-grade"
                    />
                </div>

                <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-3 min-h-[52px]">
                    <BookOpen className="w-5 h-5 text-rose-500 shrink-0" />
                    <input
                        value={form.subjects}
                        onChange={(e) => setForm({ ...form, subjects: e.target.value })}
                        placeholder="Subjects (e.g., Math, Science)"
                        className="flex-1 outline-none text-sm bg-transparent placeholder:text-slate-400"
                        data-testid="tutor-refer-subjects"
                    />
                </div>

                <button
                    type="submit"
                    disabled={busy}
                    className="w-full bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700 text-white font-bold text-sm py-3.5 rounded-xl shadow active:scale-95 transition disabled:opacity-60 inline-flex items-center justify-center gap-2"
                    data-testid="tutor-refer-submit"
                >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Submit Referral
                </button>

                <Link to="/tutor/dashboard" className="block text-center text-xs text-slate-500 mt-3">
                    <ChevronLeft className="w-3 h-3 inline" /> Back to Dashboard
                </Link>
            </form>
        </TutorMobileLayout>
    );
}
